import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import {
    LaboratoryInstitutionBreadcrumbs,
    LaboratoryInstitutionCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import { IInstitutionLaboratoryReduxFormState } from "src/reducers/reactReduxForms/institutionLaboratory";
import * as InstitutionLaboratoryActions from "../../../actions/institutionLaboratory";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    DatePickerFormInputFabric,
    SelectFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { LaboratoryInstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./LabInstitutionPage.scss";

interface LabInstitutionPageProps {
    form: Dtos.InstitutionLaboratory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: IInstitutionLaboratoryReduxFormState;
    institutionLaboratory: Dtos.InstitutionLaboratory;
    lookups: Dtos.Lookup[];
    loadingInstitutionLaboratory: boolean;
    loadInstitutionLaboratorySuccess: boolean;
    loadInstitutionLaboratoryFailure: boolean;
    loadInstitutionLaboratoryErrorCode: string;
    savingInstitutionLaboratory: boolean;
    saveInstitutionLaboratorySuccess: boolean;
    saveInstitutionLaboratoryFailure: boolean;
    creatingInstitutionLaboratory: boolean;
    createInstitutionLaboratorySuccess: boolean;
    createInstitutionLaboratoryFailure: boolean;
    validatingInstitutionLaboratory: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryClearActionCreator;
    loadInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryLoadByIdActionCreator;
    saveInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratorySaveActionCreator;
    createInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryCreateActionCreator;
    validateInstitutionLaboratory: InstitutionLaboratoryActions.IInstitutionLaboratoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.institutionLaboratory";

const LabInstitutionPage: FC<LabInstitutionPageProps> = ({
    form,
    formState,
    formProperties,
    institutionLaboratory,
    lookups,
    loadingInstitutionLaboratory,
    loadInstitutionLaboratoryFailure,
    loadInstitutionLaboratoryErrorCode,
    savingInstitutionLaboratory,
    saveInstitutionLaboratorySuccess,
    saveInstitutionLaboratoryFailure,
    creatingInstitutionLaboratory,
    validatingInstitutionLaboratory,
    validationFailures,
    permissions,
    clearInstitutionLaboratory,
    loadInstitutionLaboratory,
    saveInstitutionLaboratory,
    createInstitutionLaboratory,
    validateInstitutionLaboratory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const { institutionLaboratoryId } = useParams<{
        institutionLaboratoryId: string;
    }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);
    const institutionLaboratoryIdNumber = parseInt(institutionLaboratoryId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();


    // Load Laboratory Institution
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            institutionLaboratoryIdNumber &&
            !institutionLaboratory
        ) {
            setDrawerItems(
                LaboratoryInstitutionSubMenu(
                    laboratoryIdNumber,
                    institutionLaboratoryIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                LaboratoryInstitutionBreadcrumbs(
                    laboratoryIdNumber,
                    institutionLaboratoryIdNumber
                )
            );

            loadInstitutionLaboratory(institutionLaboratoryIdNumber);
        } else if (
            laboratoryIdNumber &&
            !institutionLaboratoryIdNumber &&
            !institutionLaboratory
        ) {
            setBreadcrumbs(
                LaboratoryInstitutionCreateBreadcrumbs(laboratoryIdNumber)
            );
            setDrawerItems([]);
            createInstitutionLaboratory(laboratoryIdNumber, null);
        } else if (institutionLaboratory && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        institutionLaboratoryIdNumber,
        institutionLaboratory,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitutionLaboratory,
        setFormInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionLaboratoryFailure) {
            navigate(`/error/${loadInstitutionLaboratoryErrorCode}`);
        }
    }, [
        loadInstitutionLaboratoryFailure,
        loadInstitutionLaboratoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            laboratoryIdNumber &&
            institutionLaboratoryIdNumber &&
            institutionLaboratory?.laboratory &&
            institutionLaboratory?.institution
        ) {
            setBreadcrumbs(
                LaboratoryInstitutionBreadcrumbs(
                    laboratoryIdNumber,
                    institutionLaboratoryIdNumber,
                    institutionLaboratory.laboratory?.name,
                    institutionLaboratory.institution?.name
                )
            );
        }
    }, [
        laboratoryIdNumber,
        institutionLaboratory,
        institutionLaboratoryIdNumber,
        setDrawerItems,
        setBreadcrumbs,
        LaboratoryInstitutionBreadcrumbs,
    ]);

    // Reload laboratory institution after saving
    useEffect(() => {
        if (!savingInstitutionLaboratory && saveInstitutionLaboratorySuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Institution Laboratory saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/labs/${laboratoryIdNumber}/institutions`);
            } else if (!institutionLaboratoryIdNumber) {
                navigate(
                    `/labs/${laboratoryIdNumber}/institutions/${institutionLaboratory?.id}/details`
                );
                clearInstitutionLaboratory();
            } else {
                loadInstitutionLaboratory(institutionLaboratoryIdNumber);
            }
        }
    }, [
        savingInstitutionLaboratory,
        saveInstitutionLaboratorySuccess,
        loadInstitutionLaboratory,
        institutionLaboratoryIdNumber,
        navigate,
        saveAndReturn,
        clearInstitutionLaboratory,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingInstitutionLaboratory && saveInstitutionLaboratoryFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingInstitutionLaboratory,
        saveInstitutionLaboratorySuccess,
        loadInstitutionLaboratory,
        institutionLaboratoryIdNumber,
    ]);

    // Cleanup effect to clear the laboratory institution on component unmount
    useEffect(() => {
        return () => {
            clearInstitutionLaboratory();
            resetForm(reduxFormName);
        };
    }, [clearInstitutionLaboratory, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.InstitutionLaboratory =
                {} as Dtos.InstitutionLaboratory;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveInstitutionLaboratory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.InstitutionLaboratory) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate(`/labs/${laboratoryIdNumber}/institutions`);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.InstitutionLaboratory =
            {} as Dtos.InstitutionLaboratory;

        loadForm(
            reduxFormName,
            Object.assign({ ...institutionLaboratory }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.InstitutionLaboratory =
                {} as Dtos.InstitutionLaboratory;

            if (!form.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            if (form && hasBeenSubmitted && !validatingInstitutionLaboratory) {
                await validateInstitutionLaboratory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                institutionLaboratoryIdNumber,
                permissions,
                Dtos.Permission.LabDepartmentUpdate,
                Dtos.Permission.LabDepartmentCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingInstitutionLaboratory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);
        const baseDisabled: boolean =
            !institutionLaboratory?.laboratory?.active;

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row py-4 px-4 align-items-center">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "institutionLaboratory",
                                        {
                                            institutionId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "institutionLaboratory",
                                                                "institutionId",
                                                                lookups
                                                            ),
                                                            form.institutionId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-9 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-1 d-flex justify-content-end">
                                    <div
                                        data-tip={`The laboratory '${institutionLaboratory?.laboratory?.name}' is inactive; therefore, the institution association can't be set to active.`}
                                    >
                                        {baseDisabled && <ReactTooltip />}
                                        {renderFormControls(
                                            form,
                                            "institutionLaboratory",
                                            {
                                                active: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType:
                                                        CheckboxFormInput,
                                                    inputDisabled:
                                                        formDisabled ||
                                                        baseDisabled,
                                                    inputProps: {
                                                        label: "Active, ",
                                                    },
                                                    inputLabel: " ",
                                                    groupClassName:
                                                        "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName:
                                                        "col-form-label col-sm-5 d-none",
                                                    fieldClassName:
                                                        "col-sm-12 generic-form-field d-flex",
                                                    changeAction: (
                                                        _model,
                                                        value,
                                                        _form,
                                                        _parentModel
                                                    ) => {
                                                        if (value) {
                                                            changeForm(
                                                                reduxFormName +
                                                                    ".dateCeased",
                                                                null
                                                            );
                                                        }
                                                        changeForm(
                                                            reduxFormName +
                                                                ".active",
                                                            value
                                                        );
                                                    },
                                                },
                                            },
                                            lookups,
                                            validationFailures,
                                            formProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-5">
                                    {renderFormControls(
                                        form,
                                        "institutionLaboratory",
                                        {
                                            dateCeased: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    baseDisabled == true
                                                        ? false
                                                        : form.active,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "institutionLaboratory",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        institutionLaboratoryIdNumber,
        permissions,
        savingInstitutionLaboratory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.LabDepartmentView}
            loading={
                loadingInstitutionLaboratory ||
                creatingInstitutionLaboratory ||
                savingInstitutionLaboratory
            }
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<LabInstitutionPageProps> => {
    let institutionLaboratory: Dtos.InstitutionLaboratory | undefined = !(
        state.institutionLaboratory.formData instanceof Array
    )
        ? state.institutionLaboratory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institutionLaboratory: institutionLaboratory!,
        loadingInstitutionLaboratory:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status ==
                RequestState.Pending,
        loadInstitutionLaboratorySuccess:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status ==
                RequestState.Success,
        loadInstitutionLaboratoryFailure:
            state.institutionLaboratory.loadState &&
            state.institutionLaboratory.loadState.status ==
                RequestState.Failure,
        loadInstitutionLaboratoryErrorCode:
            state.institutionLaboratory.loadState.errorCode,
        savingInstitutionLaboratory:
            state.institutionLaboratory.saveState &&
            state.institutionLaboratory.saveState.status ==
                RequestState.Pending,
        saveInstitutionLaboratorySuccess:
            state.institutionLaboratory.saveState &&
            state.institutionLaboratory.saveState.status ==
                RequestState.Success,
        saveInstitutionLaboratoryFailure:
            state.institutionLaboratory.saveState &&
            state.institutionLaboratory.saveState.status ==
                RequestState.Failure,
        creatingInstitutionLaboratory:
            state.institutionLaboratory.createState &&
            state.institutionLaboratory.createState.status ==
                RequestState.Pending,
        createInstitutionLaboratorySuccess:
            state.institutionLaboratory.createState &&
            state.institutionLaboratory.createState.status ==
                RequestState.Success,
        createInstitutionLaboratoryFailure:
            state.institutionLaboratory.createState &&
            state.institutionLaboratory.createState.status ==
                RequestState.Failure,
        validatingInstitutionLaboratory:
            state.institutionLaboratory.validateState &&
            state.institutionLaboratory.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.institutionLaboratory.lookups,
        form: state.reduxForms.institutionLaboratory,
        formState: state.institutionLaboratory.formState,
        formProperties: state.institutionLaboratory.formProperties,
        reduxFormState: state.reduxForms.formState.institutionLaboratory,
        validationFailures: institutionLaboratory
            ? state.institutionLaboratory.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabInstitutionPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.LoadInstitutionLaboratoryById,
            dispatch
        ),
        clearInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.Clear,
            dispatch
        ),
        saveInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.SaveInstitutionLaboratory,
            dispatch
        ),
        createInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.CreateInstitutionLaboratory,
            dispatch
        ),
        validateInstitutionLaboratory: bindActionCreators(
            InstitutionLaboratoryActions.ValidateInstitutionLaboratory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabInstitutionPage);
